import { Button, Grid } from "@material-ui/core";
import Authorize from "components/Authorize";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import DataTable from "components/DataTable/DataTable";
import LoadingSpinner from "components/LoadingSpinner";
import useSortOptions from "hooks/useSortOptions";
import { IconAdd, IconDeleteBlue, IconEdit } from "icons";
import { orderBy } from "lodash";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FilterService, { fieldTypes } from "services/filterService";
import { arrayToObject } from "utils/object-util";
import { resetFilters, setFilters } from "../QuickBooksAccountMapping/QuickBooksAccountMappingSlice";
import usePrompt from "hooks/usePrompt";
import NotificationProvider from "components/NotificationProvider";
import { entityTypeConfigMap } from "./QuickBooksMappingConsts";
import AddQBMapping from "./AddQuickBooksMapping";
import { useDeleteQuickBooksMappingMutation, useGetQuickbooksMappingByTypeQuery } from "./QuickBooksMappingSlice";


const QuickbooksMappingPage = ({ type, title}) => {

    const config = entityTypeConfigMap[type];
    const { entityKey, quickbooksKey, entityTitle, quickbooksTitle } = config;

    let { data: parqEntities, isLoading: isLoadingEntities } = config.useEntityTypeQuery();
    let { data: quickbooksData, isLoading: isLoadingQB } = config.useQuickbooksQuery();
    let { data: quickbooksParqMapping, error: mappingError, isLoading: isLoadingMapping } = useGetQuickbooksMappingByTypeQuery({ type });
    

    const isLoading = () => isLoadingEntities || isLoadingMapping || isLoadingQB;

    quickbooksParqMapping = quickbooksParqMapping?.data || [];
    parqEntities = parqEntities || [];
    quickbooksData = quickbooksData || [];
    
    const parqEntitiesLookup = arrayToObject(parqEntities);
    const quickbooksDataLookup = arrayToObject(quickbooksData);

    const mappings = quickbooksParqMapping?.map(mapping => ({
        ...mapping,
        entityName: parqEntitiesLookup[mapping?.entityId]?.[entityKey],
        quickbooksName: quickbooksDataLookup[mapping?.quickbooksId]?.[quickbooksKey],
    }))

    const [deleteMapping] = useDeleteQuickBooksMappingMutation();

    const [mappingToAdd, setMappingToAdd] = useState({entityType : type});
    const [openDialog, setOpenDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const dispatch = useDispatch();    
    const { triggerPrompt } = usePrompt();
    const [sortOptions, sortData] = useSortOptions();
    const { filters } = useSelector(state => state.quickBooksMappingFilters);
    
    const updateFilters = (prop, value) => {
        dispatch(setFilters({ ...filters, [prop]: { value } }));
    }

    const onDropFilter = (filterValue, prop) => {
        updateFilters(prop, filterValue);
    };
    
    const onResetFilters = () => {
        dispatch(resetFilters());
    };

    const quickbooksForFilter = quickbooksData.map(c => ({
    ...c,
    name: c?.[quickbooksKey] 
    }))

    const entitiesForFilter = parqEntities.map(p => ({
    ...p,
    name: p?.[entityKey] 
    }))

    let filterConfig = [
        { type: fieldTypes.TEXT, size: 2, title: entityTitle, key: 'entityId', values: entitiesForFilter },
        { type: fieldTypes.TEXT, size: 2, title: quickbooksTitle, key: 'quickbooksId', values: quickbooksForFilter },
    ];

    let tableColumns = [
        { name: entityTitle, key: "entityName" },
        { name: quickbooksTitle, key: "quickbooksName" },
    ];

    let filteredMappings = FilterService.filter(filters, null, mappings, filterConfig);

    const rowActions = (rowId) => ([
        {
            icon: <IconEdit />,
            onClick: () => onEdit(rowId),
            profile: permissionProfiles.INVOICE.EDIT_QUICKBOOKS_ACCOUNT_MAPPING
        },
        {
            icon: <IconDeleteBlue width={'18'} />,
            onClick: () => onDelete(rowId),
            profile: permissionProfiles.INVOICE.EDIT_QUICKBOOKS_ACCOUNT_MAPPING
        }
    ]);

    const onEdit = (id) => {
        setMappingToAdd(mappings?.find(m => m.id === id));
        setOpenDialog(true);
        setIsEditing(true);
    };

    const onDelete = (id) => {
        triggerPrompt({
            title: "Delete Mapping",
            content: "Are you sure you want to delete this mapping?",
            onConfirm: async () => {
                let res = await deleteMapping(id);

                if (!res?.error) {
                    NotificationProvider.success('Deleted successfully');
                } else {
                    NotificationProvider.error('Failed to delete');
                }
            },
        });
    }
    
    return (
        <Authorize profile={permissionProfiles.INVOICE.VIEW_QUICKBOOKS_ACCOUNT_MAPPING}>
            {isLoading() ? <LoadingSpinner loading={isLoading()} /> :
                <div style={{ height: 'calc(100vh - 95px)' }}>
                    <DataTable
                        columns={tableColumns}
                        rows={orderBy(
                            filteredMappings,
                            sortOptions.columnToSort,
                            sortOptions.sortDirection
                        )}
                        filters={filters}
                        onDropFilter={onDropFilter}
                        dropDownsConfig={filterConfig}
                        rowIdentifier='id'
                        onSort={sortData}
                        sortDirection={sortOptions.sortDirection}
                        columnToSort={sortOptions.columnToSort}
                        noItemsMessage='There are no mappings matching this criteria to display'
                        withFilterPanel={true}
                        onResetFilters={onResetFilters}
                        title={title}
                        actionButtons={<Authorize profile={permissionProfiles.INVOICE.EDIT_QUICKBOOKS_ACCOUNT_MAPPING}>
                            <Grid item>
                                <Button 
                                    variant='contained' 
                                    color='primary' 
                                    onClick={() => setOpenDialog(true)}
                                    startIcon={<IconAdd />}
                                >
                                    Add New Mapping
                                </Button>
                            </Grid>
                        </Authorize>}
                        actions={rowActions}
                    />
                </div>
            }
            <AddQBMapping 
                open={openDialog} setOpen={setOpenDialog}
                mappingToAdd={mappingToAdd} setMappingToAdd={setMappingToAdd} 
                mappings={mappings} parqEntities={parqEntities} parqEntitiesKey={entityKey}
                quickbooksData={quickbooksData} quickbooksKey={quickbooksKey} type={type}
                isEditing={isEditing} setIsEditing={setIsEditing}
                entityTitle={entityTitle} quickbooksTitle={quickbooksTitle}
            />
        </Authorize>
    )
}

export default QuickbooksMappingPage;